import React, { useEffect, useState } from 'react';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { DaycareReviewModal } from './DaycareReviewModal';
import { GroomingReviewModal } from './GroomingReviewModal';
import { TrainerReviewModal } from './TrainerReviewModal';
import { OrderReviewModal } from './OrderReviewModal';
import { ReviewModal } from './ReviewModal';
import { useTranslation } from 'react-i18next';

export const GlobalReviewTrigger: React.FC = () => {
    const { user } = useAuth();
    const { notifications, markAsRead } = useNotifications();
    const [showDaycareModal, setShowDaycareModal] = useState(false);
    const [showGroomingModal, setShowGroomingModal] = useState(false);
    const [showTrainerModal, setShowTrainerModal] = useState(false);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [showDoctorModal, setShowDoctorModal] = useState(false);
    const [doctorAppointment, setDoctorAppointment] = useState<any>(null);
    const [bookingId, setBookingId] = useState<string | null>(null);
    const [appointmentId, setAppointmentId] = useState<string | null>(null);
    const [trainerBookingId, setTrainerBookingId] = useState<string | null>(null);
    const [orderId, setOrderId] = useState<string | null>(null);
    const [providerName, setProviderName] = useState<string>('');
    const { t } = useTranslation();

    useEffect(() => {
        if (!user || user.role !== 'OWNER' || showDaycareModal || showGroomingModal || showTrainerModal || showOrderModal) return;

        const checkReviews = async () => {
            // 1. Check for Doctor/Vet Review (Previously in Dashboard)
            try {
                // Use the configured api instead of raw axios
                const { api } = await import('../lib/axios');
                const aptRes = await api.get('/appointments');
                const appointmentsData = aptRes.data;
                const unreviewed = appointmentsData.find((apt: any) => 
                    apt.status === 'COMPLETED' && 
                    apt.doctorId && 
                    !apt.review
                );

                if (unreviewed) {
                    const dismissed = JSON.parse(localStorage.getItem('dismissedDoctorReviews') || '[]');
                    if (!dismissed.includes(unreviewed.id)) {
                        // Delay showing the review to allow Ads or initial loading to settle
                        setTimeout(() => {
                           setDoctorAppointment(unreviewed);
                           setShowDoctorModal(true);
                        }, 3000);
                        return;
                    }
                }
            } catch (err) {
                console.error('Failed to fetch appointments for global review trigger', err);
            }

            // 2. Check for Daycare Review Notifications
            const daycareNotif = notifications.find(
                (n) => n.kind === 'DAYCARE_REVIEW' && !n.read
            );

            if (daycareNotif) {
                const match = daycareNotif.message?.match(/bookingId:([\w-]+)/);
                const extractedBookingId = match?.[1];

                if (extractedBookingId) {
                    const dismissed = JSON.parse(localStorage.getItem('dismissedDaycareReviews') || '[]');
                    if (!dismissed.includes(extractedBookingId)) {
                        const nameMatch = daycareNotif.message?.match(/stay at (.*)\?/);
                        setProviderName(nameMatch?.[1] || t('reviews.theDaycare', 'the Daycare'));
                        setBookingId(extractedBookingId);
                        setShowDaycareModal(true);
                        markAsRead(daycareNotif.id);
                        return;
                    }
                }
            }

            // 3. Check for Grooming Completion Notifications
            const groomingNotif = notifications.find(
                (n) => n.kind === 'GROOMING' && n.title?.includes('Completed') && !n.read && n.appointmentId
            );

            if (groomingNotif && groomingNotif.appointmentId) {
                const dismissed = JSON.parse(localStorage.getItem('dismissedGroomingReviews') || '[]');
                if (!dismissed.includes(groomingNotif.appointmentId)) {
                    const nameMatch = groomingNotif.message?.match(/at (.*) has been completed/);
                    setProviderName(nameMatch?.[1] || t('reviews.theGroomer', 'the Groomer'));
                    setAppointmentId(groomingNotif.appointmentId);
                    setShowGroomingModal(true);
                    markAsRead(groomingNotif.id);
                    return;
                }
            }

            // 4. Check for Training Completion Notifications
            const trainingNotif = notifications.find(
                (n) => n.kind === 'TRAINING' && n.title === 'Session Completed' && !n.read && n.trainingBookingId
            );

            if (trainingNotif && trainingNotif.trainingBookingId) {
                const tBookingId = trainingNotif.trainingBookingId;
                const dismissed = JSON.parse(localStorage.getItem('dismissedTrainerReviews') || '[]');
                if (!dismissed.includes(tBookingId)) {
                    setTrainerBookingId(tBookingId);
                    setShowTrainerModal(true);
                    markAsRead(trainingNotif.id);
                    return;
                }
            }

            // 5. Check for Order Delivery Notifications
            const orderNotif = notifications.find(
                (n) => n.kind === 'ORDER' && n.title === 'Order Delivered!' && !n.read && n.orderId
            );

            if (orderNotif && orderNotif.orderId) {
                const dismissed = JSON.parse(localStorage.getItem('dismissedOrderReviews') || '[]');
                if (!dismissed.includes(orderNotif.orderId)) {
                    setOrderId(orderNotif.orderId);
                    setShowOrderModal(true);
                    markAsRead(orderNotif.id);
                    return;
                }
            }
        };

        checkReviews();
    }, [notifications, user, showDaycareModal, showGroomingModal, showTrainerModal, showOrderModal, markAsRead]);

    const handleCloseDaycare = () => {
        if (bookingId) {
            const dismissed = JSON.parse(localStorage.getItem('dismissedDaycareReviews') || '[]');
            if (!dismissed.includes(bookingId)) {
                dismissed.push(bookingId);
                localStorage.setItem('dismissedDaycareReviews', JSON.stringify(dismissed));
            }
        }
        setShowDaycareModal(false);
        setBookingId(null);
    };

    const handleCloseGrooming = () => {
        if (appointmentId) {
            // Check which list to add to (Grooming or Doctor)
            // For safety, add to both or we can be more specific
            const dismissedG = JSON.parse(localStorage.getItem('dismissedGroomingReviews') || '[]');
            const dismissedD = JSON.parse(localStorage.getItem('dismissedDoctorReviews') || '[]');
            
            if (!dismissedG.includes(appointmentId)) dismissedG.push(appointmentId);
            if (!dismissedD.includes(appointmentId)) dismissedD.push(appointmentId);
            
            localStorage.setItem('dismissedGroomingReviews', JSON.stringify(dismissedG));
            localStorage.setItem('dismissedDoctorReviews', JSON.stringify(dismissedD));
        }
        setShowGroomingModal(false);
        setAppointmentId(null);
    };

    const handleCloseDoctor = () => {
        if (doctorAppointment) {
            const dismissed = JSON.parse(localStorage.getItem('dismissedDoctorReviews') || '[]');
            if (!dismissed.includes(doctorAppointment.id)) {
                dismissed.push(doctorAppointment.id);
                localStorage.setItem('dismissedDoctorReviews', JSON.stringify(dismissed));
            }
        }
        setShowDoctorModal(false);
        setDoctorAppointment(null);
    };

    const handleCloseTrainer = () => {
        if (trainerBookingId) {
            const dismissed = JSON.parse(localStorage.getItem('dismissedTrainerReviews') || '[]');
            if (!dismissed.includes(trainerBookingId)) {
                dismissed.push(trainerBookingId);
                localStorage.setItem('dismissedTrainerReviews', JSON.stringify(dismissed));
            }
        }
        setShowTrainerModal(false);
        setTrainerBookingId(null);
    };

    const handleCloseOrder = () => {
        if (orderId) {
            const dismissed = JSON.parse(localStorage.getItem('dismissedOrderReviews') || '[]');
            if (!dismissed.includes(orderId)) {
                dismissed.push(orderId);
                localStorage.setItem('dismissedOrderReviews', JSON.stringify(dismissed));
            }
        }
        setShowOrderModal(false);
        setOrderId(null);
    };

    return (
        <>
            {showDoctorModal && doctorAppointment && (
                <ReviewModal
                    isOpen={showDoctorModal}
                    appointment={doctorAppointment}
                    onClose={(completed?: boolean) => {
                        handleCloseDoctor();
                        if (completed) {
                            // If successfully submitted, mark as reviewed locally or force a refetch if needed
                        }
                    }}
                />
            )}
            {showOrderModal && orderId && (
                <OrderReviewModal
                    isOpen={showOrderModal}
                    orderId={orderId}
                    onClose={handleCloseOrder}
                    onSuccess={() => {
                        setShowOrderModal(false);
                        setOrderId(null);
                    }}
                />
            )}
            {showDaycareModal && bookingId && (
                <DaycareReviewModal
                    isOpen={showDaycareModal}
                    onClose={handleCloseDaycare}
                    dayCareBookingId={bookingId}
                    daycareName={providerName}
                    onSuccess={() => {
                        setShowDaycareModal(false);
                        setBookingId(null);
                    }}
                />
            )}

            {showGroomingModal && appointmentId && (
                <GroomingReviewModal
                    isOpen={showGroomingModal}
                    onClose={handleCloseGrooming}
                    appointmentId={appointmentId}
                    agencyName={providerName}
                    onSuccess={() => {
                        setShowGroomingModal(false);
                        setAppointmentId(null);
                    }}
                />
            )}

            {showTrainerModal && trainerBookingId && (
                <TrainerReviewModal
                    isOpen={showTrainerModal}
                    trainingBookingId={trainerBookingId}
                    trainerName={providerName || undefined}
                    onClose={handleCloseTrainer}
                    onSuccess={() => {
                        setShowTrainerModal(false);
                        setTrainerBookingId(null);
                    }}
                />
            )}
        </>
    );
};
